import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import { SITE } from "@/lib/site";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/philosophy", label: "Philosophy" },
  { to: "/contact", label: "Contact" },
];

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 bg-gradient-to-r from-primary via-[hsl(178_72%_30%)] to-[hsl(155_60%_36%)] ${
        scrolled ? "shadow-[0_8px_30px_-12px_hsl(var(--primary)/0.5)]" : ""
      }`}
    >
      <div className="container mx-auto">
        <div className="flex items-center justify-between gap-6 h-16 md:h-[72px]">
          {/* Logo */}
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 shrink-0 group">
            <span className="rounded-2xl bg-white/95 ring-1 ring-white/30 p-1 shadow-sm transition-transform group-hover:scale-105 pulse-ring">
              <Logo variant="mark" tone="brand" size={36} />
            </span>
            <Logo variant="wordmark" wordmarkTone="onBrand" wordmarkSize="md" />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `relative px-4 py-2 text-sm font-medium transition-colors rounded-full ${
                    isActive
                      ? "text-white"
                      : "text-white/75 hover:text-white"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <span>{l.label}</span>
                    {isActive && (
                      <span className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 h-1 w-1 rounded-full bg-white" />
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          {/* Right CTAs */}
          <div className="flex items-center gap-2">
            <a
              href={`tel:${SITE.phoneRaw}`}
              data-testid="nav-phone"
              className="hidden md:inline-flex items-center gap-2 text-xs font-medium text-white/85 hover:text-white transition-colors"
            >
              <Phone className="w-3.5 h-3.5" />
              {SITE.phone}
            </a>
            <Button
              asChild
              size="sm"
              data-testid="nav-cta-new-patient"
              className="hidden sm:inline-flex rounded-full bg-white text-primary hover:bg-white/90 font-semibold gap-1.5 px-4 shadow-sm"
            >
              <Link to="/contact">
                Schedule Appointment
              </Link>
            </Button>

            <button
              data-testid="nav-mobile-toggle"
              className="lg:hidden grid place-items-center w-10 h-10 rounded-full border border-white/30 text-white"
              onClick={() => setOpen((v) => !v)}
              aria-label="Toggle menu"
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {open && (
          <div
            data-testid="mobile-menu"
            className="lg:hidden mt-2 mb-3 rounded-3xl bg-white border border-border p-4 shadow-2xl animate-fade-up"
          >
            <nav className="flex flex-col">
              {NAV.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `flex items-center justify-between px-3 py-3 rounded-xl text-base ${
                      isActive
                        ? "bg-primary/10 text-primary"
                        : "text-foreground hover:bg-secondary"
                    }`
                  }
                >
                  <span className="font-display tracking-tight">{l.label}</span>
                  <ArrowUpRight className="w-4 h-4 opacity-60" />
                </NavLink>
              ))}
              <Button
                asChild
                data-testid="mobile-cta-new-patient"
                className="mt-3 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold w-full"
              >
                <Link to="/contact">
                  Schedule Appointment
                </Link>
              </Button>
              <a
                href={`tel:${SITE.phoneRaw}`}
                className="mt-2 text-center text-sm text-muted-foreground py-2"
              >
                Call {SITE.phone}
              </a>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
